/* eslint-disable react-hooks/rules-of-hooks */
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence, motion } from "motion/react";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconCaretRight, IconCheck } from "@/components/icons";
import { IconFaders } from "@/components/icons/player.icons";

import { cn } from "@/utils/cn";

import { PlayerUIProps } from "../UI/PlayerUI";
import { usePlayerControls2 } from "../hooks/PlayerControlsContext";

enum MenuType {
  Main = "main",
  Quality = "quality",
  Speed = "speed",
  Audio = "audio",
}

const speedOptions = ["0.25", "0.5", "0.75", "1", "1.25", "1.5", "1.75", "2"];

const SettingsButton = ({
  videoRef,
  currentQuality,
  setQuality,
  qualities,
  audioTracks,
  setAudioTrack,
  currentAudioTrack,
}: PlayerUIProps) => {
  const [activeMenu, setActiveMenu] = useState<MenuType>(MenuType.Main);
  const [curSpeed, setCurSpeed] = useState(videoRef.current?.playbackRate || "1");
  const { setControlsLock } = usePlayerControls2();
  const { t } = useTranslation();
  const [currentAudio, setCurrentAudio] = useState<number>(0);

  const openMenu = () => {
    setControlsLock(false);
    setActiveMenu(MenuType.Main);
  };

  const renderBackButton = () => (
    <div>
      <div
        className="flex cursor-pointer items-center justify-between border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]"
        onClick={() => setActiveMenu(MenuType.Main)}
      >
        <p className="flex items-center text-[18px] text-[#808080]">
          <IconCaretRight className="size-5 rotate-180 stroke-[#808080]" /> {t("player.back")}
        </p>
        <p>
          {activeMenu == MenuType.Quality
            ? t("player.quality")
            : activeMenu == MenuType.Speed
              ? t("player.speed")
              : t("player.language")}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <AnimatePresence mode="wait">
        <div className="">
          <Menu>
            {({ open, close }) => {
              const [hideTimer, setHideTimer] = useState<NodeJS.Timeout | null>(null);
              useEffect(() => {
                setControlsLock(!open);
              }, [open]);

              const handleMouseLeave = () => {
                const timer = setTimeout(() => {
                  close();
                }, 2000);
                setHideTimer(timer);
              };

              const handleMouseEnter = () => {
                if (hideTimer) {
                  clearTimeout(hideTimer);
                  setHideTimer(null);
                }
              };

              return (
                <>
                  <MenuButton as="div" onClick={openMenu} className={"outline-none"}>
                    <IconFaders className="player-icon" />
                  </MenuButton>
                  <MenuItems
                    as="div"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    className="absolute bottom-[100%] right-0 z-[100] mb-[50px] w-[304px] space-y-2 overflow-hidden rounded-2xl bg-surface py-4 outline-none transition-all max-lg:w-[200px] max-lg:py-2"
                  >
                    <motion.div
                      key={activeMenu}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.3 }}
                    >
                      {activeMenu === MenuType.Main && (
                        <>
                          {[
                            {
                              label: t("player.quality"),
                              type: MenuType.Quality,
                              default:
                                qualities && currentQuality != null && qualities[+currentQuality]
                                  ? `${qualities[+currentQuality].height}p`
                                  : "auto",
                            },
                            { label: t("player.speed"), type: MenuType.Speed, default: `${curSpeed}x` },
                            {
                              label: t("player.language"),
                              type: MenuType.Audio,
                              default: `${audioTracks?.[currentAudio]?.name}`,
                            },
                          ].map((item) => (
                            <div key={item.type}>
                              <div
                                className="flex cursor-pointer items-center justify-between border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]"
                                onClick={() => setActiveMenu(item.type)}
                              >
                                {item.label}
                                <span className="flex  items-center gap-1">
                                  <p className="text-[16px] text-[#808080]">{item.default?.toString()}</p>

                                  <IconCaretRight className="size-5 stroke-[#808080]" />
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      {activeMenu === MenuType.Quality && (
                        <>
                          {renderBackButton()}
                          {setQuality &&
                            qualities?.map((quality, index) => (
                              <MenuItem key={index}>
                                <div
                                  className="flex cursor-pointer items-center gap-2 border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]"
                                  onClick={() => {
                                    setQuality(index);
                                    setActiveMenu(MenuType.Main);
                                  }}
                                >
                                  <IconCheck
                                    className={cn("size-6 stroke-transparent", {
                                      "stroke-white": index == currentQuality,
                                    })}
                                  />
                                  {quality.height + "p"}
                                </div>
                              </MenuItem>
                            ))}
                          {setQuality && (
                            <MenuItem>
                              <div
                                className="flex cursor-pointer items-center gap-2 border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]"
                                onClick={() => setQuality("auto")}
                              >
                                <IconCheck
                                  className={cn("size-6 stroke-transparent", {
                                    "stroke-white": currentQuality == "auto",
                                  })}
                                />
                                Auto
                              </div>
                            </MenuItem>
                          )}
                        </>
                      )}

                      {activeMenu === MenuType.Speed && (
                        <>
                          {renderBackButton()}
                          {speedOptions.map((speed) => (
                            <MenuItem key={speed}>
                              <div
                                key={speed}
                                onClick={() => {
                                  if (videoRef?.current) videoRef.current.playbackRate = +speed;
                                  setCurSpeed(speed);
                                }}
                              >
                                <div className="flex cursor-pointer items-center justify-between border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]">
                                  <span>{speed}x</span>

                                  {curSpeed == +speed && (
                                    <span>
                                      <IconCheck className="size-4 stroke-white" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </MenuItem>
                          ))}
                        </>
                      )}

                      {activeMenu === MenuType.Audio && (
                        <>
                          {renderBackButton()}
                          {[...new Map(audioTracks?.map((track) => [track.id, track])).values()].map((track) => (
                            <MenuItem key={track.id}>
                              <div
                                onClick={() => {
                                  setAudioTrack?.(track.id), setCurrentAudio(track.id);
                                }}
                              >
                                <div className="flex cursor-pointer items-center justify-between border-b border-outline px-4 py-2 transition-colors hover:bg-outline max-lg:py-[4px]">
                                  <span>{track.name}</span>
                                  {track.id == currentAudioTrack && (
                                    <span>
                                      <IconCheck className="size-4 stroke-white" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </MenuItem>
                          ))}
                        </>
                      )}
                    </motion.div>
                  </MenuItems>
                </>
              );
            }}
          </Menu>
        </div>
      </AnimatePresence>
    </>
  );
};

export default SettingsButton;
